import { z } from 'zod'
import type { OnboardingListItem } from '@natalan/capehost-contracts'

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days from `from` to `to`, rounded down; null when either is missing or unreadable. */
export function daysBetween(from: string | null, to: string | Date | null): number | null {
  if (!from || !to) return null
  const start = Date.parse(from)
  const end = typeof to === 'string' ? Date.parse(to) : to.getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return null
  return Math.max(0, Math.floor((end - start) / DAY_MS))
}

/**
 * Median days from invite to submit over the properties that have both, in
 * whole days (rounded down, like each row), with how many it covers, for the Onboarding page's footer; null
 * when none has submitted yet, so the page shows no empty metric.
 */
export function medianInviteToSubmitDays(
  items: readonly { invited_at: string | null; submitted_at: string | null }[]
): { days: number; count: number } | null {
  const days = items
    .map(item => daysBetween(item.invited_at, item.submitted_at))
    .filter((d): d is number => d !== null)
    .sort((a, b) => a - b)
  if (days.length === 0) return null
  const mid = Math.floor(days.length / 2)
  const median = days.length % 2 ? days[mid] : (days[mid - 1] + days[mid]) / 2
  return { days: Math.floor(median), count: days.length }
}

/** The latest `nudged` (or, failing that, `invited`) event, for the nudge confirmation. */
export function lastEmailEvent(
  events: readonly { type: string; at: string }[] | null | undefined
): { type: 'nudged' | 'invited'; at: string } | null {
  let latest: { type: 'nudged' | 'invited'; at: string; time: number } | null = null
  for (const event of events ?? []) {
    if (event.type !== 'nudged' && event.type !== 'invited') continue
    const time = Date.parse(event.at)
    if (Number.isNaN(time)) continue
    if (!latest || time > latest.time) latest = { type: event.type, at: event.at, time }
  }
  return latest ? { type: latest.type, at: latest.at } : null
}

export type OnboardingStatusFields = Pick<
  OnboardingListItem,
  'status' | 'sections_complete' | 'sections_total' | 'changed_since_review'
> & { reviewed_at?: string | null }

/** The list's own status in words: invited, started n/12, submitted, reviewed. */
export function onboardingStatusLabel(item: OnboardingStatusFields): string {
  switch (item.status) {
    case 'invited':
      return 'Invited'
    case 'started':
      return `Started ${item.sections_complete}/${item.sections_total}`
    case 'submitted':
      return 'Submitted'
    case 'reviewed':
      return 'Reviewed'
  }
}

/**
 * One planned key move of "Link to Uplisting listing", in words. The keys
 * are admin-api's storage names, so the label says what each one is and the
 * raw keys stay available underneath for anyone checking.
 */
export function describeLinkMove(move: { from: string; to: string }): string {
  const key = move.from
  if (key.startsWith('property_document:')) return 'A document or photo'
  if (key.startsWith('ma_tax_registration:')) return 'The tax registration'
  if (key.startsWith('property_profile:by_property:')) return 'The profile'
  if (key.startsWith('property_profile:'))
    return 'The profile record (stays, now pointed at the listing)'
  if (key.startsWith('property:users:')) return 'Who has access to the property'
  if (key.startsWith('user:properties:')) return "An owner's list of properties"
  if (key.startsWith('property:')) return 'The property record (the old id keeps forwarding)'
  return 'Other data'
}

const LinkRefusalSchema = z.object({
  conflicts: z.array(z.string()),
})

/** The keys a refused link names as conflicts, when it names any. */
export function readLinkConflicts(body: unknown): string[] {
  const parsed = LinkRefusalSchema.safeParse(body)
  return parsed.success ? parsed.data.conflicts : []
}
